<template>
  <div
    class="booklist-cart-quantity-control"
    :class="{ 'booklist-cart-quantity-control--add': !cartLine && showAddButton }"
    @click.stop
  >
    <order-line-quantity-input
      v-if="showStepper"
      class="booklist-cart-quantity-control__stepper"
      :order-line-id="cartLine.id"
      :quantity="cartQuantity"
      small
      remove-at-one
      @input="handleQuantityInput"
      @quantityUpdated="handleQuantityUpdated"
      @quantityFailed="handleQuantityFailed"
    />
    <span
      v-else-if="cartLine"
      class="booklist-cart-quantity-control__static text-caption font-weight-bold"
    >
      {{ $t('checkout.qty', { count: cartQuantity }) }}
    </span>
    <v-btn
      v-else-if="showAddButton"
      small
      rounded
      elevation="0"
      class="booklist-cart-quantity-control__add"
      :loading="adding"
      :disabled="adding || disabled || cartLoading"
      :aria-label="$t('shop.addToCart')"
      @click.stop="addToCart"
    >
      <span class="booklist-cart-quantity-control__add-label">{{ $t('shop.addToCart') }}</span>
      <v-icon
        class="booklist-cart-quantity-control__add-icon"
        right
        small
      >
        $cart
      </v-icon>
    </v-btn>
  </div>
</template>

<script>
import OrderQueries from '@/graphql/OrderQueries';
import OrderLineQuantityInput from '@/components/inputs/OrderLineQuantityInput.vue';
import CurrentCartOrderMixin from '@/mixins/CurrentCartOrderMixin';
import OptimisticCartUpdateMixin from '@/mixins/optimisticCartUpdateMixin';
import { gtagAddToCartEvent } from '@/helpers/gtagHelper';
import { removeIriId } from '@/iris';
import { isPrintFormat as isPrintFormatHelper } from '@/utils/BookFormatHelper';

const MAX_QUANTITY = 999;

export default {
  name: 'BookListCartQuantityControl',
  components: {
    OrderLineQuantityInput,
  },
  mixins: [CurrentCartOrderMixin, OptimisticCartUpdateMixin],
  props: {
    productId: {
      type: String,
      required: true,
    },
    bookId: {
      type: String,
      default: null,
    },
    bookListId: {
      type: String,
      default: null,
    },
    book: {
      type: Object,
      default: null,
    },
    format: {
      type: [String, Object],
      default: null,
    },
    initialQuantity: {
      type: Number,
      default: 1,
    },
    isOwned: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      adding: false,
    };
  },
  computed: {
    normalizedProductId() {
      return removeIriId(this.productId);
    },
    resolvedBookId() {
      return this.bookId || `/books/${this.normalizedProductId}`;
    },
    countryCode() {
      return this.$store.state.locale.countryCode;
    },
    cartLoading() {
      return !!this.currentCartId && this.currentCartOrderLoading && !this.currentCartOrder;
    },
    cartLine() {
      return this.findCartLine(this.currentCartOrder);
    },
    cartQuantity() {
      return Number(this.cartLine?.quantity || 1);
    },
    order() {
      return this.currentCartOrder;
    },
    targetInitialQuantity() {
      const parsed = Number(this.initialQuantity || 1);
      if (!Number.isFinite(parsed)) return 1;
      return Math.max(1, Math.min(MAX_QUANTITY, Math.floor(parsed)));
    },
    showStepper() {
      return !!this.cartLine && this.canAdjustQuantity;
    },
    canAdjustQuantity() {
      return isPrintFormatHelper(this.cartLine?.format || this.format || this.book?.format);
    },
    showAddButton() {
      return !this.isOwned || this.canAdjustQuantity;
    },
    analyticsBook() {
      return this.book || {
        id: this.resolvedBookId,
        _id: this.normalizedProductId,
      };
    },
  },
  methods: {
    findCartLine(order) {
      const edge = order?.orderLines?.edges?.find(({ node } = {}) => {
        return node && removeIriId(node.productId) === this.normalizedProductId;
      });
      return edge?.node || null;
    },
    refreshCartData() {
      return this.$store.dispatch('fetchCartData', {
        apolloClient: this.$apollo.provider.defaultClient,
        forceNetwork: true,
      });
    },
    handleQuantityInput(quantity) {
      if (!quantity || !this.cartLine?.id) return;
      this.optimisticQuantityUpdate({
        orderLineId: this.cartLine.id,
        quantity,
      });
      this.markCartStale();
    },
    markCartStale() {
      this.$store.commit('markCartDataStale');
      this.$emit('quantityInput');
    },
    async handleQuantityUpdated() {
      this.$store.commit('markCartDataStale');
      await this.refreshCartData();
      this.$emit('cartUpdated');
    },
    async handleQuantityFailed() {
      this.$store.dispatch('snackbar/showSnackbar', {
        text: this.$t('errors.general'),
        color: 'error',
      });
      await this.refreshCartData();
      this.$emit('quantityFailed');
    },
    publishCartOrder(order) {
      if (order.id !== this.currentCartId) {
        this.$store.commit('setCurrentCart', order.id);
        this.$store.commit('setCartOwner', this.$store.state.auth.getMe);
      }
    },
    async addToCart() {
      this.adding = true;
      try {
        const result = await this.$apollo.mutate({
          mutation: this.bookListId ? OrderQueries.addMultipleItemsToOrder : OrderQueries.addItemToOrder,
          variables: this.bookListId ? {
            itemIds: [this.normalizedProductId],
            orderIri: this.currentCartId,
            locale: this.countryCode,
            bookListId: this.bookListId,
          } : {
            itemId: this.resolvedBookId,
            orderId: this.currentCartId,
            locale: this.countryCode,
          },
        });
        const order = this.bookListId
          ? result.data?.addMultipleItemsToOrder?.order
          : result.data?.addItemToOrder?.order;
        if (!order) {
          throw new Error('cart add mutation returned no order');
        }

        const publishCartOrder = () => this.publishCartOrder(order);
        const line = this.findCartLine(order);
        const shouldAdjustQuantity = line
          && isPrintFormatHelper(line.format || this.format || this.book?.format)
          && this.targetInitialQuantity > Number(line.quantity || 1);
        if (shouldAdjustQuantity) {
          try {
            await this.$apollo.mutate({
              mutation: OrderQueries.adjustQuantity,
              variables: {
                input: {
                  id: line.id,
                  newQuantity: this.targetInitialQuantity,
                },
              },
            });
          } catch (error) {
            publishCartOrder();
            this.$store.commit('markCartDataStale');
            await this.refreshCartData();
            throw error;
          }
        }

        publishCartOrder();
        this.$store.commit('markCartDataStale');
        await this.refreshCartData();
        gtagAddToCartEvent(this.analyticsBook);
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('shop.addedToCart'),
          color: 'success',
        });
        this.$emit('cartUpdated');
      } catch (error) {
        console.error('Error adding booklist item to cart:', error);
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('errors.general'),
          color: 'error',
        });
        this.$emit('error', error);
      } finally {
        this.adding = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "~vuetify/src/styles/styles.sass";

$pill-height: 34px;
$pill-height-mobile: 40px;
$add-label-inset: 12px;

// One stable pill footprint across all three states (add button, stepper,
// read-only quantity) so the control morphs in place without layout jumps.
// Card contexts (UserBookList) widen it; row contexts (PublicBookList) rely
// on the fixed width for cross-row alignment.
.booklist-cart-quantity-control {
  display: flex;
  align-items: center;
  width: 160px;
  max-width: 100%;
  min-height: $pill-height;
}

.booklist-cart-quantity-control__add,
.booklist-cart-quantity-control__static,
.booklist-cart-quantity-control .booklist-cart-quantity-control__stepper {
  width: 100%;
  border-radius: 999px;
}

.booklist-cart-quantity-control__add {
  height: $pill-height !important; // v-size--small pins height to 28px
  padding: 0 $add-label-inset !important;
  border: 0;
  background: $book-ink !important;
  color: var(--v-textOnPrimary-base) !important;
  box-shadow: none !important;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: background 0.12s, color 0.12s;
}

.booklist-cart-quantity-control__add:hover {
  background: $book-grey-dark !important;
}

.booklist-cart-quantity-control__add:focus-visible {
  outline: 2px solid rgba($book-ink, 0.4);
  outline-offset: 2px;
}

.booklist-cart-quantity-control__add ::v-deep .v-btn__content {
  justify-content: center;
  min-width: 0;
}

.booklist-cart-quantity-control__add-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.booklist-cart-quantity-control__add-icon {
  // Icon follows label (Shop AddToCartButton order: text then cart).
  margin-left: 8px !important;
  margin-right: 0 !important;
  color: inherit !important;
}


.booklist-cart-quantity-control__static {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: $pill-height;
  padding: 0 12px;
  border: 0;
  background: $book-grey-lightest;
  color: $book-grey-dark;
}

.booklist-cart-quantity-control .booklist-cart-quantity-control__stepper {
  height: $pill-height;

  border-color: rgba($book-red, 0.32);
  background: white;
  // The stepper buttons carry inline 30px sizes; grow them to proper touch
  // targets inside the pill.
  ::v-deep .qty-stepper__btn {
    width: #{$pill-height - 2px} !important;
    height: #{$pill-height - 2px} !important;
    margin: 1px;
    border-radius: 50%;
  }

  ::v-deep .qty-stepper__value {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    height: 100%;
    border-left: 0;
    border-right: 0;
  }

  ::v-deep .qty-stepper__btn:hover:not(:disabled),
  ::v-deep .qty-stepper__btn--plus:hover:not(:disabled),
  ::v-deep .qty-stepper__btn--remove:hover:not(:disabled) {
    background: rgba($book-red, 0.06);
    color: $book-red;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .booklist-cart-quantity-control {
    min-height: $pill-height-mobile;
  }

  .booklist-cart-quantity-control__add,
  .booklist-cart-quantity-control__static,
  .booklist-cart-quantity-control .booklist-cart-quantity-control__stepper {
    border-radius: 999px;
  }

  .booklist-cart-quantity-control__add {
    height: $pill-height-mobile !important;
  }

  .booklist-cart-quantity-control__static,
  .booklist-cart-quantity-control .booklist-cart-quantity-control__stepper {
    height: $pill-height-mobile;
  }

  .booklist-cart-quantity-control .booklist-cart-quantity-control__stepper ::v-deep .qty-stepper__btn {
    width: #{$pill-height-mobile - 2px} !important;
    height: #{$pill-height-mobile - 2px} !important;
  }
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  .booklist-cart-quantity-control--add {
    width: $pill-height-mobile;
    flex: 0 0 $pill-height-mobile;
  }

  .booklist-cart-quantity-control__add {
    min-width: 0 !important;
    padding: 0 !important;
  }

  .booklist-cart-quantity-control__add ::v-deep .v-btn__content {
    justify-content: center;
  }

  .booklist-cart-quantity-control__add-label {
    display: none;
  }

  .booklist-cart-quantity-control__add-icon {
    margin: 0 !important;
  }
}
</style>
